import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  House, Receipt, Wrench, Package, Users, ClockCounterClockwise, Gear, SignOut, Storefront, CaretDown, Check, CalendarCheck, ChartBar, Wallet, ArrowsLeftRight, DotsThreeOutline, X,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { Toaster } from "../components/ui/sonner";
import { api } from "../lib/api";
import ShiftGuard from "./ShiftGuard";
import IdleLogout from "./IdleLogout";
import PinPromptModal from "./PinPromptModal";
import { NotificationBell } from "./NotificationBell";
import { MorningSummary } from "./MorningSummary";
import { IncomingMessageHost } from "./IncomingMessageHost";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House },
  { to: "/pos", label: "POS", icon: Receipt },
  { to: "/repairs", label: "Repairs", icon: Wrench },
  { to: "/bookings", label: "Bookings", icon: CalendarCheck, badgeKey: "bookings" },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/trade-ins", label: "Trade-In", icon: ArrowsLeftRight },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/invoices", label: "Invoices", icon: ClockCounterClockwise },
  { to: "/till", label: "Till", icon: Wallet },
  { to: "/reports", label: "Reports", icon: ChartBar },
];

// Bottom tab bar items for phone layout (≤639px)
const BOTTOM_NAV = [
  { to: "/dashboard", label: "Home", icon: House },
  { to: "/pos", label: "POS", icon: Receipt },
  { to: "/repairs", label: "Repairs", icon: Wrench },
  { to: "/bookings", label: "Bookings", icon: CalendarCheck, badgeKey: "bookings" },
];

const MORE_NAV = [
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/trade-ins", label: "Trade-In", icon: ArrowsLeftRight },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/invoices", label: "Invoices", icon: ClockCounterClockwise },
  { to: "/till", label: "Till", icon: Wallet },
  { to: "/reports", label: "Reports", icon: ChartBar },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { stores, currentStore, setStore } = useStore();
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [pendingStoreSwitch, setPendingStoreSwitch] = useState(null);
  const [newBookingsCount, setNewBookingsCount] = useState(0);

  const onPickStore = (storeId) => {
    if (storeId === currentStore?.id) {
      setStoreMenuOpen(false);
      return;
    }
    try {
      setStore(storeId);
      setStoreMenuOpen(false);
      window.location.reload();
    } catch (e) {
      if (e.code === "PIN_REQUIRED") {
        setStoreMenuOpen(false);
        setPendingStoreSwitch(storeId);
      } else { throw e; }
    }
  };
  const nav = useNavigate();
  const loc = useLocation();

  // Close more sheet on navigation
  useEffect(() => { setMoreSheetOpen(false); }, [loc.pathname]);

  useEffect(() => {
    if (!currentStore?.id) return;
    let cancelled = false;
    const fetchCount = () =>
      api.get("/bookings/unacknowledged/count")
        .then((r) => { if (!cancelled) setNewBookingsCount(r.data?.count ?? 0); })
        .catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [currentStore?.id, loc.pathname]);

  return (
    <div className="min-h-screen flex bg-[#FFFFFF] text-[#121216]">
      {/* Desktop/Tablet Sidebar — hidden on phone (<640px) */}
      <aside className="hidden sm:flex w-16 md:w-16 lg:w-64 shrink-0 bg-[#F8FAFC] border-r border-[#E5E7EB] flex-col sticky top-0 h-screen transition-[width] duration-200" data-testid="sidebar">
        <div className="px-3 lg:px-6 pt-6 pb-4 flex items-center gap-2.5 justify-center lg:justify-start">
          <div className="w-9 h-9 rounded-xl bg-[#1E5FEB] flex items-center justify-center shrink-0">
            <Storefront size={18} weight="duotone" className="text-white" />
          </div>
          <div className="hidden lg:block font-display font-semibold text-[17px] tracking-tight text-[#121216]">
            iRepair<span className="text-[#1E5FEB]">.</span>
          </div>
        </div>

        {stores.length > 0 && (
          <div className="hidden lg:block px-4 mb-3 relative">
            <button
              onClick={() => setStoreMenuOpen((o) => !o)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-xl bg-white hover:bg-[#FFFFFF] border border-[#E5E7EB] transition-colors"
              data-testid="store-switcher"
              style={{ boxShadow: "0 2px 8px rgba(36,71,55,0.04)" }}
            >
              <div className="text-left min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-medium">Store</div>
                <div className="text-sm font-semibold truncate mt-0.5">{currentStore?.name || "—"}</div>
              </div>
              <CaretDown size={14} weight="bold" className={`transition-transform text-[#4B5563] ${storeMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {storeMenuOpen && (
              <div className="absolute top-full left-4 right-4 mt-2 bg-white rounded-xl z-20 overflow-hidden rh-fade-in"
                   style={{ boxShadow: "0 20px 48px rgba(36,71,55,0.15)" }} data-testid="store-menu">
                {stores.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => onPickStore(s.id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-[#FFFFFF] border-b border-[#F3F4F6] last:border-0"
                    data-testid={`store-opt-${s.id}`}
                  >
                    <span className="truncate">{s.name}</span>
                    {s.id === currentStore?.id && <Check size={14} weight="bold" className="text-[#1E5FEB]" />}
                  </button>
                ))}
                {user?.role === "admin" && (
                  <button
                    onClick={() => { setStoreMenuOpen(false); nav("/settings"); }}
                    className="w-full px-4 py-3 text-xs font-semibold text-left hover:bg-[#FFFFFF] bg-[#F8FAFC] text-[#1E5FEB] tracking-wider"
                  >
                    MANAGE STORES →
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <nav className="flex-1 px-2 lg:px-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, badgeKey }) => {
            const badgeCount = badgeKey === "bookings" ? newBookingsCount : 0;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${to.replace("/", "")}`}
                title={label}
                className={({ isActive }) =>
                  `flex items-center justify-center lg:justify-start gap-3 h-12 px-3 lg:px-4 rounded-xl text-[14px] font-medium transition-all relative ${
                    isActive
                      ? "bg-[#1E5FEB] text-white shadow-[0_4px_14px_rgba(36,71,55,0.18)]"
                      : "text-[#4B5563] hover:bg-[#DBEAFE] hover:text-[#1E5FEB]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={19} weight={undefined} />
                    <span className="hidden lg:inline flex-1">{label}</span>
                    {badgeCount > 0 && (
                      <span
                        data-testid={`nav-badge-${badgeKey}`}
                        className={`min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center tabular-nums shrink-0 absolute lg:relative top-1 right-1 lg:top-auto lg:right-auto ${
                          isActive
                            ? "bg-white text-[#1E5FEB]"
                            : "bg-[#DC2626] text-white animate-pulse"
                        }`}
                        style={
                          isActive
                            ? undefined
                            : { boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.15)" }
                        }
                      >
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              title="Settings"
              className={({ isActive }) =>
                `flex items-center justify-center lg:justify-start gap-3 h-12 px-3 lg:px-4 rounded-xl text-[14px] font-medium transition-all ${
                  isActive
                    ? "bg-[#1E5FEB] text-white shadow-[0_4px_14px_rgba(36,71,55,0.18)]"
                    : "text-[#4B5563] hover:bg-[#DBEAFE] hover:text-[#1E5FEB]"
                }`
              }
            >
              <Gear size={19} />
              <span className="hidden lg:inline">Settings</span>
            </NavLink>
          )}
        </nav>

        <div className="p-2 lg:p-4">
          <div className="flex items-center gap-3 px-2 py-3 rounded-xl bg-white border border-[#E5E7EB] justify-center lg:justify-start">
            <NavLink to="/profile" data-testid="sidebar-profile-link" title="My profile"
              className="flex items-center gap-3 min-w-0 lg:flex-1 rounded-lg hover:opacity-80 transition-opacity">
              <div className="w-9 h-9 rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#1E5FEB] font-semibold text-sm shrink-0" title={user?.name || ""}>
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="hidden lg:flex lg:flex-1 min-w-0 flex-col">
                <div className="text-[13px] font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
                <div className="text-[11px] text-[#9CA3AF]">My profile</div>
              </div>
            </NavLink>
            <div className="hidden lg:block"><NotificationBell panelClass="bottom-full mb-2 -left-32" /></div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="hidden lg:inline-flex p-2 rounded-lg text-[#9CA3AF] hover:bg-[#FFFFFF] hover:text-[#DC2626] transition-colors"
              title="Log out"
            >
              <SignOut size={16} weight="bold" />
            </button>
          </div>
          <div className="lg:hidden mt-2 flex justify-center"><NotificationBell panelClass="left-full ml-3 bottom-0" /></div>
          <button
            data-testid="logout-btn-compact"
            onClick={() => { logout(); nav("/login"); }}
            className="lg:hidden mt-2 w-full flex items-center justify-center h-10 rounded-xl text-[#9CA3AF] hover:bg-white hover:text-[#DC2626] transition-colors"
            title="Log out"
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>
      </aside>

      {/* Main content — add bottom padding on phone for the nav bar */}
      <div className="sm:hidden fixed top-2 right-2 z-[9001] bg-white/90 backdrop-blur rounded-xl border border-[#E5E7EB] shadow-sm">
        <NotificationBell panelClass="right-0 top-full mt-2" />
      </div>
      <MorningSummary />
      <IncomingMessageHost />
      <main className="flex-1 min-w-0 pb-[72px] sm:pb-0">
        {children}
      </main>

      {/* ===== PHONE BOTTOM NAV BAR (< 640px) ===== */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-[9000] bg-white border-t border-[#E5E7EB] flex items-center justify-around h-[68px] px-1" data-testid="bottom-nav" style={{ boxShadow: "0 -4px 24px rgba(0,0,0,0.06)" }}>
        {BOTTOM_NAV.map(({ to, label, icon: Icon, badgeKey }) => {
          const badgeCount = badgeKey === "bookings" ? newBookingsCount : 0;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={`btm-nav-${to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative transition-colors ${
                  isActive ? "text-[#1E5FEB]" : "text-[#9CA3AF]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full bg-[#1E5FEB]" />}
                  <Icon size={22} weight={isActive ? "fill" : "regular"} />
                  <span className="text-[10px] font-semibold leading-tight">{label}</span>
                  {badgeCount > 0 && (
                    <span className="absolute top-2 right-1/2 translate-x-3 min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center bg-[#DC2626] text-white" style={{ boxShadow: "0 0 0 2px white" }}>
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
        {/* More button */}
        <button
          onClick={() => setMoreSheetOpen(true)}
          data-testid="btm-nav-more"
          className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${moreSheetOpen ? "text-[#1E5FEB]" : "text-[#9CA3AF]"}`}
        >
          <DotsThreeOutline size={22} weight={moreSheetOpen ? "fill" : "regular"} />
          <span className="text-[10px] font-semibold leading-tight">More</span>
        </button>
      </nav>

      {/* ===== MORE SHEET (phone only) ===== */}
      {moreSheetOpen && (
        <div className="sm:hidden fixed inset-0 z-[9500]" data-testid="more-sheet-overlay">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMoreSheetOpen(false)} />
          <div className="absolute bottom-[68px] left-0 right-0 bg-white rounded-t-2xl overflow-hidden rh-fade-in" style={{ boxShadow: "0 -12px 40px rgba(0,0,0,0.12)" }}>
            {/* User card */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#F3F4F6]">
              <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#1E5FEB] font-semibold text-sm shrink-0">
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold truncate">{user?.name}</div>
                <div className="text-[11px] text-[#9CA3AF] capitalize">{user?.role}{currentStore ? ` · ${currentStore.name}` : ""}</div>
              </div>
              <button onClick={() => setMoreSheetOpen(false)} className="p-2 rounded-lg text-[#9CA3AF] hover:text-[#121216]">
                <X size={18} weight="bold" />
              </button>
            </div>
            {/* Nav items */}
            <div className="grid grid-cols-3 gap-1 p-3">
              {MORE_NAV.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`more-nav-${to.replace("/", "")}`}
                  onClick={() => setMoreSheetOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1.5 py-4 rounded-xl transition-colors ${
                      isActive ? "bg-[#EFF6FF] text-[#1E5FEB]" : "text-[#4B5563] hover:bg-[#F8FAFC]"
                    }`
                  }
                >
                  <Icon size={22} />
                  <span className="text-[11px] font-semibold">{label}</span>
                </NavLink>
              ))}
              {user?.role === "admin" && (
                <NavLink
                  to="/settings"
                  data-testid="more-nav-settings"
                  onClick={() => setMoreSheetOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1.5 py-4 rounded-xl transition-colors ${
                      isActive ? "bg-[#EFF6FF] text-[#1E5FEB]" : "text-[#4B5563] hover:bg-[#F8FAFC]"
                    }`
                  }
                >
                  <Gear size={22} />
                  <span className="text-[11px] font-semibold">Settings</span>
                </NavLink>
              )}
            </div>
            {/* Logout */}
            <div className="px-3 pb-4">
              <button
                onClick={() => { setMoreSheetOpen(false); logout(); nav("/login"); }}
                data-testid="more-logout-btn"
                className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border border-[#E5E7EB] text-[#DC2626] text-[13px] font-semibold hover:bg-red-50 transition-colors"
              >
                <SignOut size={16} weight="bold" /> Log out
              </button>
            </div>
          </div>
        </div>
      )}

      <Toaster position="top-right" richColors />
      <ShiftGuard />
      <IdleLogout />

      <PinPromptModal
        open={!!pendingStoreSwitch}
        title="Admin PIN required to switch store"
        description="Staff are pinned to their assigned store. Ask an admin to enter their PIN to switch."
        statusEndpoint="/admin-pin/status"
        onClose={() => setPendingStoreSwitch(null)}
        onConfirm={async (pin) => {
          await api.post("/admin-pin/verify", { pin });
          setStore(pendingStoreSwitch, { pinVerified: true });
          setPendingStoreSwitch(null);
          toast.success("Store switched");
          window.location.reload();
        }}
      />
    </div>
  );
}

export function PageHeader({ overline, title, actions, titleTestId }) {
  return (
    <div className="px-4 sm:px-5 md:px-6 lg:px-10 pt-4 sm:pt-6 md:pt-8 lg:pt-10 pb-3 sm:pb-4 md:pb-5 lg:pb-6 flex items-end justify-between gap-2 sm:gap-3 flex-wrap bg-[#FFFFFF]">
      <div>
        {overline && <div className="overline mb-1 sm:mb-2">{overline}</div>}
        <h1 data-testid={titleTestId} className="font-display font-semibold text-[22px] sm:text-[26px] md:text-3xl lg:text-[44px] tracking-tight leading-none text-[#121216]">{title}</h1>
      </div>
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    </div>
  );
}
